/**
 * Turns crucible-levels validator findings (stable codes + engineering messages) into
 * designer-facing language. The core package stays technical; this is the presentation layer.
 */
import type { LevelFinding } from '@volley/crucible-levels';

export interface HumanFinding {
  title: string;
  fix?: string;
  /** The original technical message, kept as a muted detail line. */
  detail: string;
  severity: LevelFinding['severity'];
}

const AT = (f: LevelFinding): string => {
  const anchor = f.at?.vertexId ?? f.at?.roomId;
  return anchor ? ` (near ${anchor})` : '';
};
const AT2 = (f: LevelFinding): string => (f.at?.levelId ? ` (${f.at.levelId})` : '');

const MAP: Record<string, (f: LevelFinding) => { title: string; fix?: string }> = {
  'ledge-unreachable': (f) => ({
    title: `This jump is too far or too high to clear${AT(f)}`,
    fix: 'Shorten the gap, or lower the landing point.',
  }),
  'fence-too-tall': (f) => ({
    title: `This wall is too tall to jump over${AT(f)}`,
    fix: 'Lower the wall.',
  }),
  'fence-too-wide': (f) => ({
    title: `This wall is too wide to clear in one jump${AT(f)}`,
    fix: 'Make it thinner or shorter.',
  }),
  'fence-insufficient-runway': (f) => ({
    title: `There's no room to line up the jump over this wall${AT(f)}`,
    fix: 'Add or lengthen a flat platform right before the wall so players can build up.',
  }),
  'platform-too-narrow': (f) => ({
    title: `This platform is too small to land on${AT(f)}`,
    fix: 'Make it wider.',
  }),
  'platform-too-narrow-for-group': (f) => ({
    title: `This platform is too small for the whole team${AT(f)}`,
    fix: 'Widen it so all four roped players fit.',
  }),
  'tether-group-too-wide-for-window': (f) => ({
    title: `The four roped players can't fit through this spot at once${AT(f)}`,
    fix: 'Widen this section.',
  }),
  'tether-cannot-bunch-in-time': (f) => ({
    title: `The team can't squeeze together fast enough before this spot${AT(f)}`,
    fix: 'Give them more flat ground beforehand, or widen this section.',
  }),
  'reversal-too-long-for-group': (f) => ({
    title: `This backwards-sprint is too long for the whole team to finish in time${AT(f)}`,
    fix: 'Shorten the reversal, or give it a longer time window.',
  }),
  'collapse-outruns-dragged-group': (f) => ({
    title: `The collapsing floor is faster than the team can run${AT(f)}`,
    fix: "Slow the collapse — they can't outrun it while dragging a downed player.",
  }),
  'missing-three-cs': () => ({
    title: 'The feel (3Cs) profile needs to be set first (or matches a different game type)',
    fix: 'Open “3Cs profile” in the Levels list, pick the right archetype, and press Save.',
  }),
  // topdown-2d (floor plans)
  'corridor-too-narrow': (f) => ({
    title: `This corridor is too tight to walk through${AT(f)}`,
    fix: 'Widen the corridor.',
  }),
  'room-too-small': (f) => ({
    title: `This room is too small for the character${AT(f)}`,
    fix: 'Make the room bigger.',
  }),
  'room-exceeds-camera': (f) => ({
    title: `This room is bigger than the camera can show${AT(f)}`,
    fix: 'Shrink the room, or accept scrolling in both directions.',
  }),
  'goal-unreachable': (f) => ({
    title: `The goal can never be reached from the start${AT(f)}`,
    fix: 'Connect it with corridors — and make sure any key needed is pickable first.',
  }),
  'door-key-unreachable': (f) => ({
    title: `A locked door's key can never be picked up${AT(f)}`,
    fix: 'Move the key to a room reachable before the door.',
  }),
  'room-unreachable': (f) => ({
    title: `This room can't be reached${AT(f)}`,
    fix: 'Connect it with a corridor (or delete it).',
  }),
  'pacing-under-target': () => ({
    title: 'The level plays much shorter than its time target',
    fix: 'Add rooms, enlarge them, or lower the target seconds.',
  }),
  // progression (campaign structure)
  'progression-missing-level': (f) => ({
    title: `The progression references a level that doesn't exist${AT2(f)}`,
    fix: 'Remove the entry or restore the level.',
  }),
  'progression-duplicate-entry': (f) => ({
    title: `A level appears twice in the progression${AT2(f)}`,
    fix: 'Remove the duplicate entry.',
  }),
  'progression-unknown-requirement': (f) => ({
    title: `An unlock requirement points at a level that isn't in the progression${AT2(f)}`,
    fix: 'Pick requirements from levels placed earlier.',
  }),
  'progression-unknown-set': (f) => ({
    title: `A world/set is referenced but not defined${AT2(f)}`,
    fix: 'Define the set, or clear the reference.',
  }),
  'level-never-unlockable': (f) => ({
    title: `This level can never be unlocked${AT2(f)}`,
    fix: 'Its requirements form a loop or depend on something unreachable — untangle them.',
  }),
  'level-not-in-progression': (f) => ({
    title: `A level isn't placed in the progression${AT2(f)}`,
    fix: 'Add it below, or it simply won’t appear in the game.',
  }),
  schema: (f) => ({ title: `Level structure problem${AT(f)}`, fix: undefined }),
  // data-table
  'weights-do-not-sum': () => ({
    title: "Drop chances don't add up to 100%",
    fix: 'Adjust the weights so they total 1.',
  }),
  'weight-out-of-range': () => ({
    title: 'A drop chance is outside 0–100%',
    fix: 'Use a value between 0 and 1.',
  }),
  'weight-not-positive': () => ({
    title: 'A weight is zero or negative',
    fix: 'Use a positive weight.',
  }),
  'mixed-weighting': () => ({
    title: 'Some rows have weights and some don’t',
    fix: 'Give every row a weight, or none.',
  }),
  'orphan-ref': () => ({
    title: 'A row points at an item that doesn’t exist',
    fix: 'Fix the reference or add the item to Content.',
  }),
  'duplicate-row-id': () => ({
    title: 'Two rows share the same id',
    fix: 'Make each row id unique.',
  }),
  'missing-required-field': () => ({ title: 'A row is missing a required field', fix: undefined }),
};

export function humanizeFinding(f: LevelFinding): HumanFinding {
  const mapped = MAP[f.code]?.(f);
  return {
    title: mapped?.title ?? f.message,
    fix: mapped?.fix,
    detail: f.message,
    severity: f.severity,
  };
}
